import type { AgentTask, TaskExecution } from '~/server/agents/types'
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import type { SupabaseClient } from '@supabase/supabase-js'

export interface TaskExecutionWithAgent extends TaskExecution {
  agent_task: AgentTask
}

export async function fetchAgentTasks(companyId: string) {
  console.log("Fetching agent tasks for company:", companyId)
  const { $supabase } = useNuxtApp()
  const supabase = $supabase as SupabaseClient
  const agentTasks = useAgentTasks()

  agentTasks.value.isLoading = true
  agentTasks.value.error = null

  try {
    const { data, error } = await supabase
      .from('task_executions')
      .select(`
        *,
        agent_task:agent_tasks(*)
      `)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error("Error fetching task executions:", error)
      throw error
    }

    console.log("Fetched task executions:", data)
    agentTasks.value.tasks = data as TaskExecutionWithAgent[]
  } catch (err) {
    console.error('Error in fetchAgentTasks:', err)
    agentTasks.value.error = 'Failed to load task execution data'
  } finally {
    agentTasks.value.isLoading = false
  }
}

export async function handleRealtimeAgentTaskUpdate(payload: RealtimePostgresChangesPayload<TaskExecutionWithAgent>) {
  console.log("Realtime task execution update received:", payload)
 
  const { $supabase } = useNuxtApp()
  const supabase = $supabase as SupabaseClient
  const agentTasks = useAgentTasks()

  const { eventType, new: newRecord, old: oldRecord } = payload

  // Function to fetch complete execution data with agent task
  const fetchCompleteExecutionData = async (executionId: string) => {
    console.log('Fetching complete execution data for:', executionId)
    const { data, error } = await supabase
      .from('task_executions')
      .select(`
        *,
        agent_task:agent_tasks(*)
      `)
      .eq('id', executionId)
      .single()

    if (error) {
      console.error("Error fetching complete execution data:", error)
      return null
    }

    return data as TaskExecutionWithAgent
  }

  switch (eventType) {
    case 'INSERT':
      if (newRecord) {
        const completeExecution = await fetchCompleteExecutionData(newRecord.id)
        if (completeExecution) {
          agentTasks.value.tasks = [...agentTasks.value.tasks, completeExecution]
        }
      }
      break

    case 'UPDATE':
      if (newRecord) {
        const completeExecution = await fetchCompleteExecutionData(newRecord.id)
        if (completeExecution) {
          agentTasks.value.tasks = agentTasks.value.tasks.map((execution: TaskExecutionWithAgent) => 
            execution.id === completeExecution.id ? completeExecution : execution
          )
        }
      }
      break

    case 'DELETE':
      if (oldRecord) {
        agentTasks.value.tasks = agentTasks.value.tasks.filter((execution: TaskExecutionWithAgent) => 
          execution.id !== oldRecord.id
        )
      }
      break
  }
} 